/* UTILS */
import router from "@/router"
import { setup, createActor, fromPromise } from "xstate"
import { storeToRefs } from "pinia"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { COOKIES } from "@/config/constants"
import { globalActor } from "@/machines/globalMachine"

/* STORES */
import { useUserStore } from "@/stores/user"
import { useAnswersStore } from "@/features/screening/shared/store/answers.store"
import { useConfigStore } from "@/stores/config"

/* HELPERS */
import { useSessionStorage } from "@/helpers/useSessionStorage"
import { useGotoParam } from "@/composables/useGoto"
import { usePendingUser } from "@/composables/usePendingUser"
import { withAssessmentLoadingMinDuration } from "@/features/assessment-loading/assessmentLoadingDuration"

/* TYPES */
import type { GlobalMachineEvent } from "@/machines/types"
import type { NextAssessmentData } from "@/types/next-assessment"

const { removeGotoParam } = useGotoParam()

type LoadScreeningDataResult =
  | { type: "consent_needed"; data?: NextAssessmentData }
  | { type: "redirected"; data?: NextAssessmentData }
  | { type: "assessment"; data?: NextAssessmentData }

// Loader for Screening datas
const loadScreeningCvData = fromPromise((): Promise<LoadScreeningDataResult> =>
  withAssessmentLoadingMinDuration("SCAN", async () => {
    const userStore = useUserStore()
    const { fetchScreeningData } = useAnswersStore()
    const { isConsentNeeded } = usePendingUser()

    const { nextAssessment } = storeToRefs(userStore)

    if (isConsentNeeded.value) {
      return { type: "consent_needed" }
    }

    if (!nextAssessment.value) {
      await fetchScreeningData()
    }

    const assessmentData = nextAssessment.value?.next_assessment

    if (!nextAssessment.value || !assessmentData?.assessment) {
      useSessionStorage().deleteSessionItem("origin")
      removeGotoParam()
      throw new Error("NO_NEXT_ASSESSMENT")
    }

    if (assessmentData?.assessment?.toLowerCase() !== "screening_cv") {
      useSessionStorage().deleteSessionItem("origin")
      removeGotoParam()

      return { type: "redirected", data: assessmentData }
    }

    return { type: "assessment", data: assessmentData }
  })
)

export const screeningCvMachine = setup({
  actors: {
    loadScreeningCvData,
  },
  types: {} as {
    events: any
    context: {
      errorSource?: string
    }
  },
  actions: {
    setErrorSource: ({ context }, params: { source: string }) => {
      context.errorSource = params.source
    },
  },
  guards: {
    checkNoPmUuid: () => {
      const pm_uuid = useCookies().get(COOKIES.PM_UUID)
      return !pm_uuid
    },
  },
}).createMachine({
  id: "screeningCvMachine",
  initial: "loading",
  states: {
    loading: {
      entry: [
        () => {
          if (router.currentRoute.value.name !== "screeningCvLoading") {
            router.replace({ name: "screeningCvLoading" })
          }
        },
      ],
      invoke: {
        src: "loadScreeningCvData",
        onDone: [
          {
            guard: ({ event }) => event.output?.type === "consent_needed",
            target: "intro",
          },
          {
            guard: ({ event }) => event.output?.type === "redirected",
            target: "final",
          },
          {
            target: "checkConditions",
          },
        ],
        onError: {
          actions: () => {
            globalActor.send({ type: "GLOBAL.SCREENING_CV.DONE" })
          },
        },
      },
    },
    checkConditions: {
      always: [
        { target: "noPmUuid", guard: "checkNoPmUuid" },
        { target: "intro" },
      ],
    },
    noPmUuid: {
      entry: [
        () => {
          globalActor.send({ type: "GLOBAL.SCREENING_CV.DONE" }) // We cannot access screening if we don't have a pm_uuid
        },
      ],
    },
    intro: {
      entry: [
        removeGotoParam,
        () => router.replace({ name: "screeningCvIntro" }),
      ],
      on: {
        done: { target: "upload" },
      },
    },
    upload: {
      entry: [
        removeGotoParam,
        () => router.replace({ name: "screeningCvForm" }),
      ],
      on: {
        done: { target: "loadResults" },
      },
    },
    loadResults: {
      entry: [
        () => useSessionStorage().deleteSessionItem("origin"),
        removeGotoParam,
        () => router.replace({ name: "screeningCvLoadResults" }),
      ],
      on: {
        done: { target: "final" },
        error: {
          target: "screeningError",
          actions: {
            type: "setErrorSource",
            params: { source: "results" },
          },
        },
      },
    },
    screeningError: {
      entry: [
        () => useSessionStorage().deleteSessionItem("origin"),
        ({ context }) =>
          router.replace({
            name: "screeningCvError",
            query: context.errorSource
              ? { source: context.errorSource }
              : undefined,
          }),
      ],
    },
    final: {
      type: "final",
    },
  },
})

export let screeningCvActor = createActor(screeningCvMachine)

const screeningCvActorObserver = {
  async complete() {
    const userStore = useUserStore()
    const cookies = useCookies()
    const { fetchScreeningData } = useAnswersStore()
    const { nextAssessmentName, user } = storeToRefs(userStore)
    const { logoutAndRedirectToAuth } = usePendingUser()
    const { clearScreeningCookies } = useAnswersStore()

    useSessionStorage().deleteSessionItem("origin")
    removeGotoParam()

    await fetchScreeningData()
    const isGuestAcc = user.value.permissions.includes("gu_acc")
    const userEmail = user.value.user.email

    if (!nextAssessmentName.value) {
      const cookiesDomain = useConfigStore().urls.cookie_domain
      const cookieOptions = { path: "/", domain: cookiesDomain }
      const cookiesToClear = [COOKIES.PM_UUID, COOKIES.COMPANY_UUID]
      cookiesToClear.forEach((cookie) => cookies.remove(cookie, cookieOptions))

      globalActor.send({ type: "GLOBAL.SCREENING_CV.DONE" })
      return
    }

    if (["meeting", "screening_interview"].includes(nextAssessmentName.value)) {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
      return
    }

    if (isGuestAcc) {
      // Guest user cannot pass other assessment make him register
      clearScreeningCookies()
      logoutAndRedirectToAuth(userEmail)
    } else {
      await userStore.fetchAssessmentSummary()
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    }
  },
}

screeningCvActor.subscribe(screeningCvActorObserver)

/**
 * Observateurs extérieurs, réabonnés à chaque acteur neuf.
 *
 * Sans ça, un abonné qui garde la référence de l'acteur — le store d'aide, qui
 * en tire le nom de l'assessment courant, donc les CTA du header — resterait
 * branché sur l'acteur remplacé et deviendrait sourd.
 */
const screeningCvActorObservers: Array<(_snapshot: unknown) => void> = []

export function subscribeToScreeningCvActor(
  observer: (_snapshot: unknown) => void
) {
  screeningCvActorObservers.push(observer)
  screeningCvActor.subscribe(observer)
}

let hasScreeningCvActorStarted = false

/**
 * XState ignore `start()` sur un acteur déjà démarré (ou arrivé à son état
 * final) : aucune action d'entrée ne rejoue, la machine ne réapplique donc
 * jamais sa route et l'utilisateur reste sur la route parente nue, écran
 * blanc. Le cas se produit dès qu'on entre deux fois dans l'assessment au
 * cours de la même page — sortie par « non passé », ou renvoi dessus par le
 * workflow. On repart donc d'un acteur neuf.
 *
 * Mais UNIQUEMENT en entrant vraiment dans l'assessment, c'est-à-dire sur la
 * route parente nue, celle par laquelle la machine globale y fait entrer. Le
 * composant racine est aussi remonté sans qu'on change d'assessment — App.vue
 * reconstruit tout `main` quand la langue change — et repartir de zéro là
 * relancerait le test depuis son écran de chargement.
 */
export function startScreeningCvActor() {
  const isEnteringAssessment = router.currentRoute.value.name === "screeningCv"

  if (hasScreeningCvActorStarted && isEnteringAssessment) {
    screeningCvActor.stop()
    screeningCvActor = createActor(screeningCvMachine)
    screeningCvActor.subscribe(screeningCvActorObserver)
    for (const observer of screeningCvActorObservers)
      screeningCvActor.subscribe(observer)
  }

  hasScreeningCvActorStarted = true
  screeningCvActor.start()
}
